/**
 * Role-Based Access Control (RBAC) Utility
 * Centralized access control system for HR AI Screening application
 */

/**
 * Defines role hierarchy and access levels
 * Higher numbers indicate higher access levels
 */
export const ROLE_HIERARCHY = {
  'admin': 100,
  'hr_manager': 80,
  'recruiter': 60,
  'hr_coordinator': 40,
  'hr_user': 20,
  'candidate': 10  // Candidate users have lowest access level
};

/**
 * Defines feature access permissions for each role
 */
export const ROLE_PERMISSIONS = {
  // Admin has full access to everything
  'admin': {
    dashboards: ['manager', 'recruiter', 'coordinator'],
    features: ['candidates', 'cv-processing', 'interviews', 'user-management', 'analytics', 'reports', 'jobs', 'tasks'],
    quickActions: ['top-candidates', 'today-interviews', 'team-management'],
    candidateActions: ['view', 'edit', 'delete', 'export', 'add-to-pipeline'],
    cvProcessing: ['upload', 'process', 'view-history', 'bulk-process', 'configure-ai'],
    interviews: ['schedule', 'conduct', 'view-all', 'edit', 'cancel'],
    userManagement: ['create-users', 'edit-users', 'delete-users', 'view-all-users'],
    jobs: ['view-jobs', 'create-jobs', 'manage-jobs', 'delete-jobs'],
    tasks: ['view-tasks', 'create-tasks', 'manage-all-tasks', 'delete-tasks'],
    advanced: ['system-settings', 'backup', 'analytics-export']
  },

  // HR Manager has strategic oversight and team management
  'hr_manager': {
    dashboards: ['manager', 'recruiter', 'coordinator'],
    features: ['candidates', 'cv-processing', 'interviews', 'user-management', 'analytics', 'reports', 'jobs', 'tasks'],
    quickActions: ['top-candidates', 'today-interviews', 'team-management'],
    candidateActions: ['view', 'edit', 'delete', 'export', 'add-to-pipeline'],
    cvProcessing: ['upload', 'process', 'view-history', 'bulk-process', 'configure-ai'],
    interviews: ['schedule', 'conduct', 'view-all', 'edit', 'cancel'],
    userManagement: ['create-users', 'edit-users', 'view-all-users'], // Cannot delete users
    jobs: ['view-jobs', 'create-jobs', 'manage-jobs', 'delete-jobs'],
    tasks: ['view-tasks', 'create-tasks', 'manage-all-tasks', 'delete-tasks'],
    advanced: ['analytics-export']
  },

  // Recruiter focuses on candidate sourcing and interviewing
  'recruiter': {
    dashboards: ['recruiter'],
    features: ['candidates', 'cv-processing', 'interviews', 'jobs', 'tasks'],
    quickActions: ['top-candidates', 'today-interviews'],
    candidateActions: ['view', 'edit', 'add-to-pipeline'],
    cvProcessing: ['upload', 'process', 'view-history'],
    interviews: ['schedule', 'conduct', 'view-own', 'edit-own'],
    userManagement: [], // No user management access
    jobs: ['view-jobs', 'create-jobs', 'manage-jobs'], // Cannot delete jobs
    tasks: ['view-tasks', 'create-tasks'], // Can view and create tasks but not manage all
    advanced: []
  },

  // HR Coordinator handles administrative tasks and scheduling
  'hr_coordinator': {
    dashboards: ['coordinator'],
    features: ['dashboard', 'candidates', 'interviews', 'tasks'],
    quickActions: ['today-interviews'],
    candidateActions: ['view', 'edit'],
    cvProcessing: [], // Limited CV processing access
    interviews: ['schedule', 'view-all', 'edit'],
    userManagement: [], // No user management access
    jobs: ['view-jobs'], // Can only view jobs
    tasks: ['view-tasks', 'create-tasks'], // Can view and create tasks
    advanced: []
  },

  // Candidate has limited self-service access
  'candidate': {
    dashboards: ['candidate'],
    features: ['profile', 'applications', 'jobs-view'],
    quickActions: ['view-applications', 'update-profile'],
    candidateActions: [], // No access to other candidates
    cvProcessing: [], // No direct CV processing access
    interviews: ['view-own'], // Can only view their own interviews
    userManagement: [], // No user management access
    jobs: ['view-jobs', 'apply-jobs'], // Can view and apply to jobs
    tasks: [], // No task access
    advanced: []
  }
};

/**
 * Route access control mapping
 */
export const ROUTE_ACCESS = {
  // HR Routes
  '/hr-roles/manager': ['admin', 'hr_manager'],
  '/hr-roles/recruiter': ['admin', 'hr_manager', 'recruiter'],
  '/hr-roles/coordinator': ['admin', 'hr_manager', 'hr_coordinator', 'hr_user'],
  '/hr-dashboard': ['admin', 'hr_manager', 'hr_coordinator', 'hr_user'],
  '/candidate-database': ['admin', 'hr_manager', 'recruiter', 'hr_coordinator', 'hr_user'],
  '/cv-upload-processing': ['admin', 'hr_manager', 'recruiter'],
  '/interview-scheduling': ['admin', 'hr_manager', 'recruiter', 'hr_coordinator'],
  '/user-management': ['admin', 'hr_manager'],
  '/job-management': ['admin', 'hr_manager', 'recruiter', 'hr_coordinator', 'hr_user'],
  '/job-management/create': ['admin', 'hr_manager', 'recruiter'],
  '/analytics': ['admin', 'hr_manager'],
  '/reports': ['admin', 'hr_manager'],
  
  // Candidate Routes
  '/candidate/dashboard': ['candidate'],
  '/candidate/profile': ['candidate'],
  '/candidate/applications': ['candidate'],
  '/candidate/jobs': ['candidate'],
  '/public/candidate-dashboard': ['candidate'],
  '/public/jobs': ['candidate'],
  '/public/track-application': ['candidate'],
  
  // Public Routes (accessible to all authenticated users)
  '/public/jobs': ['admin', 'hr_manager', 'hr_coordinator', 'hr_user', 'recruiter', 'candidate'],
};

/**
 * Check if user has access to a specific feature
 * @param {string} userRole - User's role
 * @param {string} feature - Feature to check access for
 * @returns {boolean} - Whether user has access
 */
export const hasFeatureAccess = (userRole, feature) => {
  if (!userRole) return false;
  
  const permissions = ROLE_PERMISSIONS[userRole];
  if (!permissions) return false;
  
  return permissions.features.includes(feature);
};

/**
 * Check if user has access to a specific dashboard
 * @param {string} userRole - User's role
 * @param {string} dashboard - Dashboard to check access for
 * @returns {boolean} - Whether user has access
 */
export const hasDashboardAccess = (userRole, dashboard) => {
  if (!userRole) return false;
  
  const permissions = ROLE_PERMISSIONS[userRole];
  if (!permissions) return false;
  
  return permissions.dashboards.includes(dashboard);
};

/**
 * Check if user has access to a specific route
 * @param {string} userRole - User's role
 * @param {string} route - Route to check access for
 * @returns {boolean} - Whether user has access
 */
export const hasRouteAccess = (userRole, route) => {
  if (!userRole) return false;
  
  const allowedRoles = ROUTE_ACCESS[route];
  if (!allowedRoles) return true; // Allow access if route not explicitly restricted
  
  return allowedRoles.includes(userRole);
};

/**
 * Check if user can perform a specific action
 * @param {string} userRole - User's role
 * @param {string} actionType - Type of action (candidateActions, cvProcessing, etc.)
 * @param {string} action - Specific action to check
 * @returns {boolean} - Whether user can perform action
 */
export const hasActionAccess = (userRole, actionType, action) => {
  if (!userRole) return false;
  
  const permissions = ROLE_PERMISSIONS[userRole];
  if (!permissions) return false;
  
  const actionPermissions = permissions[actionType];
  if (!actionPermissions) return false;
  
  return actionPermissions.includes(action);
};

/**
 * Get all features accessible to a user role
 * @param {string} userRole - User's role
 * @returns {Array} - Array of accessible features
 */
export const getAccessibleFeatures = (userRole) => {
  if (!userRole) return [];
  
  const permissions = ROLE_PERMISSIONS[userRole];
  return permissions ? permissions.features : [];
};

/**
 * Get all dashboards accessible to a user role
 * @param {string} userRole - User's role
 * @returns {Array} - Array of accessible dashboards
 */
export const getAccessibleDashboards = (userRole) => {
  if (!userRole) return [];
  
  const permissions = ROLE_PERMISSIONS[userRole];
  return permissions ? permissions.dashboards : [];
};

/**
 * Get all quick actions accessible to a user role
 * @param {string} userRole - User's role
 * @returns {Array} - Array of accessible quick actions
 */
export const getAccessibleQuickActions = (userRole) => {
  if (!userRole) return [];
  
  const permissions = ROLE_PERMISSIONS[userRole];
  return permissions ? permissions.quickActions : [];
};

/**
 * Check if one role has higher access than another
 * @param {string} role1 - First role
 * @param {string} role2 - Second role
 * @returns {boolean} - Whether role1 has higher access than role2
 */
export const hasHigherAccess = (role1, role2) => {
  const hierarchy1 = ROLE_HIERARCHY[role1] || 0;
  const hierarchy2 = ROLE_HIERARCHY[role2] || 0;
  
  return hierarchy1 > hierarchy2;
};

/**
 * Filter navigation items based on user role
 * @param {Array} navigationItems - Array of navigation items
 * @param {string} userRole - User's role
 * @returns {Array} - Filtered navigation items
 */
export const filterNavigationByRole = (navigationItems, userRole) => {
  if (!userRole || !navigationItems) return navigationItems;
  
  return navigationItems.filter(item => {
    // Check if item has specific role requirements
    if (item.requiredRoles) {
      return item.requiredRoles.includes(userRole);
    }
    
    // Check route access if item has a path
    if (item.path) {
      return hasRouteAccess(userRole, item.path);
    }
    
    // Check feature access if item has a feature key
    if (item.featureKey) {
      return hasFeatureAccess(userRole, item.featureKey);
    }
    
    // Allow access by default if no restrictions specified
    return true;
  });
};

/**
 * Get user's default dashboard route based on role
 * @param {string} userRole - User's role
 * @returns {string} - Default dashboard route
 */
export const getDefaultDashboard = (userRole) => {
  const roleRoutes = {
    'admin': '/hr-dashboard',
    'hr_manager': '/hr-roles/manager',
    'recruiter': '/hr-roles/recruiter',
    'hr_coordinator': '/hr-roles/coordinator',
    'hr_user': '/hr-roles/coordinator',
    'candidate': '/public/candidate-dashboard'
  };
  
  return roleRoutes[userRole] || '/hr-dashboard';
};

/**
 * Check if user should see role indicator in UI
 * @param {string} userRole - User's role
 * @returns {boolean} - Whether to show role indicator
 */
export const shouldShowRoleIndicator = (userRole) => {
  return userRole && userRole !== 'admin';
};

/**
 * Format role name for display
 * @param {string} userRole - User's role
 * @returns {string} - Formatted role name
 */
export const formatRoleName = (userRole) => {
  if (!userRole) return 'Unknown Role';
  
  const roleNames = {
    'admin': 'Administrator',
    'hr_manager': 'HR Manager',
    'recruiter': 'Recruiter',
    'hr_coordinator': 'HR Coordinator',
    'hr_user': 'HR User',
    'candidate': 'Candidate'
  };
  
  return roleNames[userRole] || userRole.replace('_', ' ').toUpperCase();
};

/**
 * Check if user is a candidate
 * @param {string} userRole - User's role
 * @returns {boolean} - Whether user is a candidate
 */
export const isCandidateUser = (userRole) => {
  return userRole === 'candidate';
};

/**
 * Check if user is an HR user
 * @param {string} userRole - User's role
 * @returns {boolean} - Whether user is an HR user
 */
export const isHRUser = (userRole) => {
  return ['admin', 'hr_manager', 'recruiter', 'hr_coordinator', 'hr_user'].includes(userRole);
};

export default {
  ROLE_HIERARCHY,
  ROLE_PERMISSIONS,
  ROUTE_ACCESS,
  hasFeatureAccess,
  hasDashboardAccess,
  hasRouteAccess,
  hasActionAccess,
  getAccessibleFeatures,
  getAccessibleDashboards,
  getAccessibleQuickActions,
  hasHigherAccess,
  filterNavigationByRole,
  getDefaultDashboard,
  shouldShowRoleIndicator,
  formatRoleName,
  isCandidateUser,
  isHRUser
};